import React, { useRef, useState } from 'react';
import genStyles from './generale.module.css';
import GeocoderService from '../service/GeocoderService';
import DrawService from '../service/DrawService';
import { useSetRecoilState } from 'recoil';
import { entiteFileState, loadingState, messageAfficheState, showResultFormState, statusOpenentiteFileState } from '../atoms/GeocoderState';

export default function GeocoderInterface({
    api_collection,
    api_pci,
    ban_uri,
    user_data,
    fileTypesLinks,
    iddCollectionSelected}) {
    const fileInputRef = useRef(null);
    const [fileName, setFileName] = useState(null);     
    const [error, setError] = useState(null);
    const [showErrorBanner, setShowErrorBanner] = useState(false);
    const [showBottomError, setShowBottomError] = useState(false);
    const [isHovering, setIsHovering] = useState(false);   
    const setLoading = useSetRecoilState(loadingState);
    const setShowResultForm = useSetRecoilState(showResultFormState); 
    const setEntitesFile = useSetRecoilState(entiteFileState);
    const setOpenFilesStates = useSetRecoilState(statusOpenentiteFileState);
    const setMessageAffiche = useSetRecoilState(messageAfficheState);
    

    const urlApi = api_collection + user_data.account.port + '/';
    const userId = user_data.user.id;

    const allowedExtensions = ["csv", "kml", "gpx", "geojson", "gpkg", "json"];

    
    // Retourne la classe FA selon l'extension
    const getFileExtension = (namefile) => {
        const ext = namefile.split(".").pop().toLowerCase();
        return ext;
    };

    const validateFile = (file) => {
        const ext = getFileExtension(file.name);
        const MAX_FILE_SIZE = 500 * 1024; // 500 Mo
        if (!allowedExtensions.includes(ext)) {
            setError(`Le type du fichier est invalide (CSV, KML, GPX, GEOJSON, GPKG) uniquement`);

             // Affiche le bandeau rouge
             setShowErrorBanner(true);
             setShowBottomError(false);
 
             // Enlève le bandeau après 2s et affiche petit message
             setTimeout(() => {
                 setShowErrorBanner(false);
                 setShowBottomError(true);
             }, 2000);
             
            setFileName(null);
            return false;
        }
        if (file.size > MAX_FILE_SIZE) {
            setError(`Le fichier dépasse la taille maximale de 500 ko.`);
            
             // Affiche le bandeau rouge
             setShowErrorBanner(true);
             setShowBottomError(false);

            // Enlève le bandeau après 2s et affiche petit message
            setTimeout(() => {
                setShowErrorBanner(false);
                setShowBottomError(true);
            }, 2000);

            setFileName(null);
            return false;
        }
    
        setError(null);
        setShowErrorBanner(false);
        setShowBottomError(false);
        return true;
    };
    
    // Quand on clique sur la zone => ouvre le sélecteur
    const handleClickFile = () => {
        fileInputRef.current.click();
    };

    // Quand un fichier est choisi
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;  
        if (validateFile(file)) {
            setFileName(file);
        }
    };

    // Quand un fichier est déposé
    const handleDropFile = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (!file) return;
        if (validateFile(file)) {
            setFileName(file);
        }
    };

    const handleDragOverFile = (e) => {
        e.preventDefault(); // important pour autoriser le drop
    };

    const manageDroppedFile = () => {
        setLoading(true);
        setMessageAffiche('');
        const extfile = getFileExtension(fileName.name);
        switch (extfile) {
            case "csv":
                manageDroppedFileCsv();
                break;
        
            case "geojson":
            case "json":
            case "kml":
            case "gpx":
                manageDroppedFileKmlGeojson();
                break;
        
            case "gpkg":
                manageDroppedFileGpkg();
                break;
        
            default:
                console.error("Type de fichier non supporté :", extfile);
        }
    };

    const manageDroppedFileKmlGeojson = () => {
        const extfile = getFileExtension(fileName.name);
		var reader = new FileReader();
        reader.readAsText(fileName);
		reader.onload = function(event) {
            var data = event.target.result;
            switch (extfile) {            
                case "geojson":
                case "json":
                    setDataGeojson(data);
                break;
                case "kml":
                    setDataKml(data);
                break;
                case "gpx":
                    setDataGpx(data);
                break;
            }
        };
        reader.onerror = errorHandler;
    };

    const manageDroppedFileGpkg = () => {
       GeocoderService.getGeojsongpkg(urlApi,userId,fileName).then((response) => { 
            if(response && response.features && response.features.length > 0){
                formatJsonResult(response);
            } else {
                errorHandler();
            } 
            
        }); 
    };

    const manageDroppedFileCsv = async () => {
        try {
            // Parse le CSV avec PapaParse
            const parsed = await new Promise((resolve, reject) => {
                Papa.parse(fileName, {
                    delimiter: ';',
                    complete: result => resolve(result),
                    error: err => reject(err)
                });
            });
    
            // On enlève les lignes vides
            const data = parsed.data.filter(row => row.some(cell => cell && cell.trim() !== ""));
    
            if (!data || data.length < 2) {                
                setShowResultForm(true);
                const detailMessage = `<p class="red-text">Aucune donnée dans le CSV</p>`;
                setMessageAffiche(detailMessage);
                setLoading(false);
                return;
            }
    
            // Détection du type de CSV
            const typeData = GeocoderService.detectCsvType(data);
            
            let geojsonResult = null;
    
            if (typeData === "points") {
                // Transformation points CSV -> GeoJSON
                geojsonResult = GeocoderService.parsePointsCsv(data);
    
            } else if (typeData === "parcelles") {
                // Transformation parcelles CSV -> GeoJSON (async)
                const parcellesArray = await GeocoderService.parseParcellesCsv(data,api_pci);
    
                // Lignes valides (parcelles trouvées)
                const validItems = parcellesArray.filter(item => 
					item.result && 
					item.result.features && 
					item.result.features.length > 0
				);

                // Conversion en FeatureCollection                /
                const features = validItems.map(item => {
                    // L'API retourne une FeatureCollection
                    const feature = item.result.features[0];
                  
                    feature.properties = {
                      ...feature.properties,
                      ...item.source
                    };
                  
                    return feature;
                });
    
                geojsonResult = {
                    type: "FeatureCollection",
                    features,
                    meta: {
                        totalRows: data.length - 1,  
                        importedRows: validItems.length,
                        errorRows: (data.length - 1) - validItems.length  
                    }
                };
            }else if (typeData === "adresses") {
                geojsonResult = await GeocoderService.parseAdressesCsv(data,ban_uri);
            } else {                              
                setShowResultForm(true);
                const detailMessage = `<p class="red-text">erreurs survenues : Mauvais format de CSV</p>`;
                setMessageAffiche(detailMessage);
                setLoading(false);
                return;
            }
    
            // Ici tu peux utiliser ton geojsonResult, par exemple
            formatJsonResult(geojsonResult);
    
        } catch (err) {        
            setShowResultForm(true);
            const detailMessage = `<p class="red-text">erreurs survenues : Erreur lors du traitement du fichier</p>`;
            setMessageAffiche(detailMessage);
            setLoading(false);
        }
    };
    
    const formatJsonResult = async(geojsonData) => {
        let layerJson = [];
        let { totalRows, importedRows, errorRows } = geojsonData.meta;
        let detailMessage = '';
        geojsonData.features.forEach(feature => {
			const element = {
				source: feature.properties || {}, 
				result: {
					type: "FeatureCollection",
					features: [feature]   // on emballe dans un FeatureCollection
				}
			};
			const valret = GeocoderService.getLayerJsonfromdata(element);
            layerJson.push(valret);
		});
        const bounds = L.latLngBounds([]);
        const newOpenStates = {};
        const layersToAdd = [];
        
        const layergroup = await DrawService.addOriginLayerFile(layerJson,iddCollectionSelected,userId);        
        const difret = totalRows - layergroup.length;
        if( difret > 0){
            errorRows = errorRows + difret;
            importedRows = importedRows - difret;
        } 
        layergroup.forEach((objlayer) => {  
            if (!objlayer) return;
    
            const layer = objlayer.data; 
            if (layer) layersToAdd.push(layer);
    
            newOpenStates[layer.id] = true;
    
            if (typeof objlayer.getBounds === "function") {
                bounds.extend(objlayer.getBounds());
            } else if (typeof objlayer.getLatLng === "function") {
                bounds.extend(objlayer.getLatLng());
            }
        });
        
        if (bounds.isValid()) {
            // Décaler le zoom pour éviter l'erreur "loaded"
            setTimeout(() => {
                DrawService.setZoomEmprise(bounds);
            }, 0);
        }
        
        if (layersToAdd.length > 0) {
            setEntitesFile((prev) => [...prev, ...layersToAdd]);
        }
    
        setOpenFilesStates((prevos) => ({
            ...prevos,
            ...newOpenStates
        }));

        setShowResultForm(true);
        
        if (errorRows === 0) {
            detailMessage = `
              <p class="green-text bold">Aucune erreur survenue</p>
              <p class="grey-text text-darken-1">
                ${importedRows} ligne${importedRows > 1 ? "s" : ""} importée${importedRows > 1 ? "s" : ""} sur ${totalRows}
              </p>
            `;
          } else {
            detailMessage = `
              <p class="orange-text bold">
                ${importedRows} ligne${importedRows > 1 ? "s" : ""} importée${importedRows > 1 ? "s" : ""}
                sur ${totalRows}
              </p>
              <p class="red-text bold">
                erreur${errorRows > 1 ? "s" : ""} survenue${errorRows > 1 ? "s" : ""} :
               ${errorRows} ligne${errorRows > 1 ? "s" : ""} non importée${errorRows > 1 ? "s" : ""}
              </p>
            `;
          }
        
       
        setMessageAffiche(detailMessage);
        setLoading(false);

    };

    const setDataGeojson = (data) => {
        let geojson;
        try {
            geojson = JSON.parse(data);
        } catch (err) {
            setShowResultForm(true);
            const detailMessage = `<p class="red-text">erreurs survenues : Mauvais format de GEOJSON</p>`;
            setMessageAffiche(detailMessage);
            setLoading(false);
            return; 
        }
        
        const meta = {
            totalRows: geojson.features.length,  
            importedRows: geojson.features.length,
            errorRows: 0 
        };
        geojson.meta = meta;

        if(geojson && geojson.features && geojson.features.length > 0){
            formatJsonResult(geojson);
        } else {           
            setShowResultForm(true);
            const detailMessage = `<p class="red-text">erreurs survenues : Mauvais format de GEOJSON</p>`;
            setMessageAffiche(detailMessage);
            setLoading(false);
        }
	};

    const setDataKml = (data) => {        
        const parser = new DOMParser();
        let geojson;
        try {
            const kmlDoc = parser.parseFromString(data, "text/xml");
            geojson = toGeoJSON.kml(kmlDoc);
        } catch (err) {
            setShowResultForm(true);
            const detailMessage = `<p class="red-text">erreurs survenues : Mauvais format de KML</p>`;
            setMessageAffiche(detailMessage);
            setLoading(false);
            return; 
        }

        const meta = {
            totalRows: geojson.features.length,  
            importedRows: geojson.features.length,
            errorRows: 0 
        };
        geojson.meta = meta;

        if(geojson && geojson.features && geojson.features.length > 0){
            formatJsonResult(geojson);
        } else {
            setShowResultForm(true);
            const detailMessage = `<p class="red-text">erreurs survenues : Mauvais format de KML</p>`;
            setMessageAffiche(detailMessage);
            setLoading(false);
        }
	};

    const setDataGpx = (data) => {
        const parser = new DOMParser();
        let geojson;
        try {
            const gpxDoc = parser.parseFromString(data, "text/xml");
            geojson = toGeoJSON.gpx(gpxDoc);
        } catch (err) {
            setShowResultForm(true);
            const detailMessage = `<p class="red-text">erreurs survenues : Mauvais format de GPX</p>`;
            setMessageAffiche(detailMessage);
            setLoading(false);
            return; 
        }       
       
        const meta = {
            totalRows: geojson.features.length,  
            importedRows: geojson.features.length,
            errorRows: 0 
        };
        geojson.meta = meta;

        if(geojson && geojson.features && geojson.features.length > 0){
            formatJsonResult(geojson);
        } else {
            setShowResultForm(true);
            const detailMessage = `<p class="red-text">erreurs survenues : Mauvais format de GPX</p>`;
            setMessageAffiche(detailMessage);
            setLoading(false);
        }
	};

    const errorHandler = () => {
        setShowResultForm(true);
        const detailMessage = `<p class="red-text">erreurs survenues : Erreur lors du traitement du fichier</p>`;
        setMessageAffiche(detailMessage);
        setLoading(false);
		return;
	};

    return (
        <div className="row">
            <div className="col s12 center-align">
                <p className="center-align">
                    Ce module permet de charger des <b>données géographiques</b> aux formats GPX, KML, GeoJSON, GPKG, et de géocoder des <b>données non géographiques</b> à partir de tableurs CSV.
                </p>	

                <p className="justify">
                    3 types de fichiers <b>CSV</b> peuvent être géocodés :
                </p>
            </div>
            <div className={`${genStyles.geocoderHelpCards} col s12`}>
                <div className={genStyles.geocoderHelpCard}>
                    <div className={genStyles.geocoderHelpHeader}>
                        <div className={genStyles.geocoderHelpIcon}>
                            <i className="fas fa-home"></i>
                        </div>
                        <h4><a className="blue-text underline tooltipped" data-tooltip="Télécharger un fichier exemple" href={fileTypesLinks.address} download>Adresses</a> </h4>
                    </div>
                    <p className="italic font_size_14" style={{margin:0}}> <b>3</b> champs nécessaires</p>
                    <p style={{margin:0}}><code>adresse</code></p>
                    <p style={{margin:0}}><code>code postal</code> | <code>cp</code> | <code>code_postal</code></p>
                    <p style={{margin:0}}><code>ville</code> | <code>commune</code> | <code>com</code> | <code>nom_commune</code></p>
                </div>

                <div className={genStyles.geocoderHelpCard}>
                    <div className={genStyles.geocoderHelpHeader}>
                        <div className={genStyles.geocoderHelpIcon}>
                            <i className="fas fa-map-marker-alt"></i>
                        </div>
                        <h4><a className="blue-text underline tooltipped" data-tooltip="Télécharger un fichier exemple" href={fileTypesLinks.point} download>Coordonnées géographiques</a> </h4>
                    </div>
                    <p className="italic font_size_14" style={{margin:0}}> <b>2</b> champs nécessaires</p>
                    <p style={{margin:0}}><code>latitude</code> | <code>lat</code> | <code>y</code></p>
                    <p style={{margin:0}}><code>longitude</code> | <code>lng</code> | <code>lon</code> | <code>long</code> | <code>x</code></p>
                </div>

                <div className={genStyles.geocoderHelpCard}>
                    <div className={genStyles.geocoderHelpHeader}>
                        <div className={genStyles.geocoderHelpIcon}>
                            <i className="fas fa-map"></i>
                        </div>
                        <h4><a className="blue-text underline tooltipped" data-tooltip="Télécharger un fichier exemple" href={fileTypesLinks.parcel} download>Parcelles cadastrales</a> <span className="font_size_12" /></h4>
                    </div>
                    <p className="italic font_size_14" style={{margin:0}}> <b>1</b> champ nécessaire</p>
                    <p style={{margin:0}}><code>parcelle</code> | <code>idpar</code> | <code>IDU</code> | <code>parcelles</code> </p>
                </div>

            </div>
            <p className="italic center-align font_size_14">
                Si le CSV contient un champ <b>titre</b> et/ou <b>observation</b>, ceux-ci seront conservés dans la collection.
            </p>
            <div className="col s12">
                <div
                    onClick={handleClickFile}
                    onDrop={handleDropFile}
                    onDragOver={handleDragOverFile}
                    className={genStyles.gcdUpForm}
                >

                    {/* === Bandeau rouge temporaire === */}
                    {showErrorBanner && (
                        <div className={genStyles.gcdErrorUp}>
                            {error}
                        </div>
                    )}

                    {fileName ? (
                        <div                    
                            onMouseEnter={() => setIsHovering(true)}
                            onMouseLeave={() => setIsHovering(false)}
                        >
                            <i className={`${genStyles.gcdIconFile}`}></i>
                            <span className={genStyles.gcdIconFileText}>{getFileExtension(fileName.name)}</span>
                            {isHovering && (
                                <div className={genStyles.gcdHoveringUp}>
                                    {/* Nom fichier en haut */}
                                    <div className={genStyles.hoverTop}>
                                    {fileName.name}
                                    <button
                                        className={genStyles.gcdbtnHoveringUp}
                                        onClick={(e) => {
                                        e.stopPropagation(); // empêche le click de remonter au div parent
                                        setFileName(null);
                                        setError(null);
                                        setShowBottomError(false);
                                        }}
                                    >
                                        SUPPRIMER
                                    </button>
                                    </div>
                            
                                    {/* Ligne de séparation */}
                                    <div className={genStyles.hoverDivider}></div>
                            
                                    {/* Texte en bas */}
                                    <div className={genStyles.hoverBottom}>
                                    Glissez puis déposez un fichier ici ou cliquez
                                    </div>
                                </div>
                                )}
                        </div>
                    ) : (
                        <div>
                            <i
                                className="fa-solid fa-cloud-arrow-up"
                                style={{ color: "#86b9e7", fontSize: "30px" }}
                            ></i>
                            <div className="italic">
                                Cliquez ou glissez-déposez un fichier ici.
                            </div>
                        </div>
                    )}
                </div>

                {/* Input caché */}
                <input
                    className={genStyles.inputtGeocd}
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                    accept=".csv,.kml,.gpx,.geojson,.gpkg"
                />
                {fileName ? (
                        <button
                            className={`${genStyles.gcdbtnLaunch} btn green lighten-1 z-depth-0 mr5 ml5`}
                            onClick={() => {
                                manageDroppedFile();
                                setShowBottomError(false);
                            }}
                        >
                            LANCER LE GÉOCODAGE
                        </button>
                    ) : (
                        showBottomError && (
                            <p style={{ color: "#d32f2f", marginTop: "5px", textAlign: "center" }}>
                                Oups, il semblerait qu’un problème soit survenu.
                            </p>
                        )
                    )}
            </div>
        </div>
    );
}
